import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { type z } from 'zod';

import { SecaoFormulario } from '@/ui/patterns/secao-formulario';
import { Button } from '@/ui/primitives/button';
import { Campo, Input } from '@/ui/primitives/input';
import { Select } from '@/ui/primitives/select';
import {
  type AtualizarEmpresaRequest,
  atualizarEmpresaRequestSchema,
  type EmpresaDetalhe,
} from '@fiscoai/contracts';

import { useAtualizarEmpresa } from './api';
import { gravar, nulo } from './formulario';
import { CRT, opcoesDe } from './rotulos';

function valoresIniciais(e: EmpresaDetalhe): AtualizarEmpresaRequest {
  return {
    razaoSocial: e.razaoSocial,
    nomeFantasia: e.nomeFantasia,
    inscricaoMunicipal: e.inscricaoMunicipal,
    crt: e.crt,
    email: e.email,
    telefone: e.telefone,
    inicioAtividades: e.inicioAtividades,
  };
}

export function AbaIdentificacao({
  empresa,
  podeEditar,
}: {
  empresa: EmpresaDetalhe;
  podeEditar: boolean;
}) {
  const atualizar = useAtualizarEmpresa(empresa.id);
  const form = useForm<
    z.input<typeof atualizarEmpresaRequestSchema>,
    unknown,
    AtualizarEmpresaRequest
  >({
    resolver: zodResolver(atualizarEmpresaRequestSchema),
    values: valoresIniciais(empresa),
  });
  const erros = form.formState.errors;
  const enviar = form.handleSubmit((dados) =>
    gravar(() => atualizar.mutateAsync(dados), 'Identificação salva'),
  );

  return (
    <SecaoFormulario
      titulo="Identificação"
      descricao="Como a empresa aparece nos documentos. O CNPJ não muda depois do cadastro."
      aoEnviar={() => void enviar()}
      acoes={
        podeEditar &&
        form.formState.isDirty && (
          <>
            <Button
              variante="fantasma"
              onClick={() => {
                form.reset();
              }}
            >
              Descartar
            </Button>
            <Button type="submit" variante="primario" carregando={atualizar.isPending}>
              Salvar
            </Button>
          </>
        )
      }
    >
      <fieldset disabled={!podeEditar} className="grid gap-4 sm:grid-cols-2">
        <legend className="sr-only">Identificação</legend>
        <Campo rotulo="Razão social" erro={erros.razaoSocial?.message} className="sm:col-span-2">
          {(a) => <Input {...a} {...form.register('razaoSocial')} />}
        </Campo>
        <Campo rotulo="Nome fantasia" erro={erros.nomeFantasia?.message}>
          {(a) => <Input {...a} {...form.register('nomeFantasia', nulo)} />}
        </Campo>
        <Campo rotulo="Inscrição municipal" erro={erros.inscricaoMunicipal?.message}>
          {(a) => <Input {...a} {...form.register('inscricaoMunicipal', nulo)} />}
        </Campo>
        <Campo
          rotulo="CRT"
          ajuda="Código de Regime Tributário usado na NF-e."
          erro={erros.crt?.message}
        >
          {(a) => (
            <Controller
              control={form.control}
              name="crt"
              render={({ field }) => (
                <Select
                  {...a}
                  opcoes={opcoesDe(CRT)}
                  valor={field.value ?? ''}
                  aoMudar={field.onChange}
                  placeholder="Não informado"
                />
              )}
            />
          )}
        </Campo>
        <Campo rotulo="Início das atividades" erro={erros.inicioAtividades?.message}>
          {(a) => <Input type="date" {...a} {...form.register('inicioAtividades', nulo)} />}
        </Campo>
        <Campo rotulo="E-mail fiscal" erro={erros.email?.message}>
          {(a) => <Input type="email" {...a} {...form.register('email', nulo)} />}
        </Campo>
        <Campo rotulo="Telefone" erro={erros.telefone?.message}>
          {(a) => <Input type="tel" {...a} {...form.register('telefone', nulo)} />}
        </Campo>
      </fieldset>
    </SecaoFormulario>
  );
}
